import { useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { NoItemContent } from "../page-bases/NoItemContent";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import ItemInput from "../../components/Input/ItemInput";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import {
  ICreateAssetDispatch,
  IDistributionBatchRecord,
  IPagination,
  IQueryPerams,
} from "../../config/types";
import { usePagination } from "../../hooks/usePagination";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";
import { distributionDispatchApi } from "../../services/distribution/distribution-dispatch";

import { DatePicker } from "../../components/calender";
import dayjs from "dayjs";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { formatDate } from "../../utils/common";
import { AssetDistributionLogsFilter } from "../../common-ui/page-filters/AssetDistributionLogsFilter";
import appRoutePath from "../../config/path";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import {
  COURIER_TRANSPORT_MODES,
  VEHICLE_TYPES,
} from "../../config/constants/common";
import { useAppSelector } from "../../store/hooks";
import { systemUser } from "../../config/constants/system-users";

function NewDispatchPage() {
  const [pageState, setPageState] = useState<string>("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const account = useAppSelector((data) => data.authVerify);
  const isSuperAdmin = account.role === systemUser.SUPER_ADMIN;

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetBatchDistributionApi.getBatchDistributions);

  const { form, updateForm, fieldHasError, updateInputField, onFormSubmit } =
    useDmsForm<ICreateAssetDispatch>({
      asset_distribution_batches_ids: [],
      courier_service_provider: "",
      package_description: "",
      dispatch_date: "",
      additional_remarks: "",
      courier_transport_mode: "Land",
      vehicle_type: "",
      vehicle_number: "",
      name_of_courier: "",
      contact_of_courier: "(233)",
    });

  const { selectedRowItems, toggleRow, toggleSelectAllRow } =
    useMangeTableItems(dataset);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const populateDataSet = (
    data: IDistributionBatchRecord[],
    pagination?: IPagination
  ) => {
    const formatData = data.map((data) => ({
      ...data,
      created_at: formatDate(data.created_at),
      from_location: ` ${data.from_location_domain}(${data.from_location})`,
      to_location: `${data.to_location_domain}(${data.to_location} )`,
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    // const result = await assetBatchDistributionApi.getBatchDistributions({
    //   is_deleted: false,
    //   distribution_batch_status: "pending",
    // });
    // TODO: Update to fetch based on user role
    let result;
    if (isSuperAdmin) {
      result = await assetBatchDistributionApi.getBatchDistributions();
    } else {
      result = await assetBatchDistributionApi.getBatchDistributions({
        is_deleted: false,
        distribution_batch_status: "pending",
        from_location_id: account.domain_id,
      });
    }
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {}, [selectedRowItems.length]);

  const navigate = useNavigate();
  function addNewItem() {}

  function clearSearch() {
    setPageState("empty-db");
  }

  async function onSaveItemClick() {
    actionDialogStore.open({
      title: "Asset Dispatch",
      subtitle: dialogMessage(selectedRowItems),
      onRightBtnPress: confirmUserSave,
    });
  }
  async function confirmUserSave() {
    const { isValid, data } = onFormSubmit({
      excludeFields: ["asset_distribution_batches_ids", "additional_remarks"],
    });
    if (isValid && selectedRowItems[0]) {
      const result = await distributionDispatchApi.createDispatch({
        ...data,
        asset_distribution_batches_ids: selectedRowItems.map((data) => data.id),
      });
      alertDialogStore.open({
        title: "Asset Dispatch",
        subtitle: result.message,
        onLeftBtnPress: result.success ? onSeeAllPress : () => {},
        leftBtnTxt: result.success ? "See All" : "Cancel",
        isSuccess: result.success,
      });
    } else {
      alertDialogStore.open({
        title: selectedRowItems[0]
          ? "Asset Dispatch Form"
          : "Batch Distribution Selection Required",
        subtitle: selectedRowItems[0]
          ? "Please ensure all required fields are completed before proceeding."
          : "Select a distributed record from the table below to continue.",
        onLeftBtnPress: () => {},
        isSuccess: false,
        rightBtnTxt: "Okay",
        leftBtnTxt: "Close",
      });
    }
  }
  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      dispatch_date: { value: date, isValid: true },
    }));
  };
  function onSeeAllPress() {
    alertDialogStore.open({
      title: "Discard Action",
      subtitle: "Click Continue to return to the Dispatch main page.",
      isSuccess: false,
      onRightBtnPress: () => navigate(appRoutePath.ASSET_DISPATCH_HISTORY_PAGE),
    });
  }

  return (
    <MainPageBase
      pageState={"main"}
      title={"Asset Dispatch "}
      addItemBtnOnClick={onSaveItemClick}
      addItemBtnText={"Create Dispatch"}
      cancelBtnClick={onSeeAllPress}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={""}
            subtitle={""}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"}>
            <Box mb={3} display={"flex"} justifyContent={"space-between"}>
              <Stack direction={"row"} display={"flex"} spacing={5}>
                <Stack direction={"column"} width={500} spacing={2}>
                  <Stack gap={3} direction={"row"}>
                    <ItemInput
                      label="Courier Service Provider"
                      placeholder="Enter the name of the courier service"
                      handleChange={updateInputField(
                        "courier_service_provider"
                      )}
                      value={form.courier_service_provider.value}
                      error={fieldHasError("courier_service_provider")}
                      errorMessage={form.courier_service_provider.errorMessage}
                      required={true}
                    />
                    <DatePicker
                      handleOnchange={handleDatePicker}
                      label="Dispatch Scheduled Date"
                      error={fieldHasError("dispatch_date")}
                      errorMessage={form.dispatch_date.errorMessage}
                      value={dayjs(form.dispatch_date.value)}
                      required={true}
                    />
                  </Stack>
                  <ItemInput
                    label="Package Description"
                    placeholder="Provide a detailed description of the package"
                    handleChange={updateInputField("package_description")}
                    value={form.package_description.value}
                    error={fieldHasError("package_description")}
                    errorMessage={form.package_description.errorMessage}
                    multiline
                    rows={5}
                    size="full"
                    required={true}
                  />
                  <Stack gap={3} direction={"row"}>
                    <ItemSelectInput
                      label="Vehicle Type"
                      placeholder="Select vehicle type"
                      items={VEHICLE_TYPES}
                      item=""
                      value={form.vehicle_type.value}
                      error={fieldHasError("vehicle_type")}
                      handleChange={updateInputField("vehicle_type")}
                      errorMessage={form.vehicle_type.errorMessage}
                      required={true}
                    />
                    <ItemInput
                      label="Vehicle Number"
                      placeholder="Enter the vehicle number"
                      handleChange={updateInputField("vehicle_number")}
                      value={form.vehicle_number.value}
                      error={fieldHasError("vehicle_number")}
                      errorMessage={form.vehicle_number.errorMessage}
                      multiline
                      rows={1}
                      required={true}
                    />
                  </Stack>
                </Stack>
                <Stack direction={"column"} width={500} spacing={2}>
                  <ItemSelectInput
                    label="Courier Transport Mode"
                    placeholder="Select courier transport mode"
                    items={COURIER_TRANSPORT_MODES}
                    item=""
                    value={form.courier_transport_mode.value}
                    error={fieldHasError("courier_transport_mode")}
                    handleChange={updateInputField("courier_transport_mode")}
                    errorMessage={form.courier_transport_mode.errorMessage}
                    required={true}
                    size="full"
                  />

                  <ItemInput
                    label="Additional Remarks"
                    placeholder="Provide any relevant additional information"
                    handleChange={updateInputField("additional_remarks")}
                    value={form.additional_remarks?.value}
                    error={fieldHasError("additional_remarks")}
                    multiline
                    rows={5}
                    size="full"
                  />

                  <Stack gap={3} direction={"row"}>
                    <ItemInput
                      label="Name of Courier"
                      placeholder="Name of the courier"
                      handleChange={updateInputField("name_of_courier")}
                      value={form.name_of_courier.value}
                      error={fieldHasError("name_of_courier")}
                      multiline
                      rows={1}
                      // size="full"
                      errorMessage={form.name_of_courier.errorMessage}
                      required={true}
                    />
                    <ItemInput
                      label="Contact of Courier"
                      placeholder="Contact of the courier"
                      handleChange={updateInputField("contact_of_courier")}
                      value={form.contact_of_courier.value}
                      error={fieldHasError("contact_of_courier")}
                      errorMessage={form.contact_of_courier.errorMessage}
                      multiline
                      rows={1}
                      required={true}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <AssetDistributionLogsFilter
              ftn={assetBatchDistributionApi.getBatchDistributions}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
              status={false}
              isDeleted={false}
              default_status="pending"
            />
            <DamsTable
              tableTitle="Distributions"
              tableSubtitle=""
              tableTitleBadge={`${selectedRowItems.length} asset${
                selectedRowItems.length <= 1 ? "" : "s"
              } selected`}
              useTableCheckbox={true}
              hasHeaderCheckBox={false}
              selectedRowItems={selectedRowItems}
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={6}
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}
const dialogMessage = (selectedRowItems: TRowItem[]) => {
  return selectedRowItems[0]
    ? `You are about to initiate an asset dispatch for batch distributions. Click 'Confirm' to continue.`
    : "Please select a distribution record from the table below to proceed.";
};

const tkeys = ["from_location", "to_location", "quantity", "created_at"];

const columns = ["From", "Destination", "Quantity", "Date Created"];

export default NewDispatchPage;
